// src/pages/Login.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { auth, db } from '../firebase';
import { signOut, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { FirebaseError } from 'firebase/app';
import { Mail, Lock, Eye, EyeOff, LogIn, ArrowLeft, AlertCircle, Loader2, CheckCircle } from 'lucide-react';

// ✅ Safe environment check for development
const isDevelopment = process.env.NODE_ENV === 'development';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Forgot Password Modal
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');
  
  // Ref for timeout cleanup
  const forgotTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (forgotTimeoutRef.current) {
        clearTimeout(forgotTimeoutRef.current);
      }
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // ✅ Prevent duplicate submissions
    if (loading) return;
    
    setError('');
    
    // ✅ Normalize email but keep password exact
    const normalizedEmail = email.trim().toLowerCase();
    
    // Validate email format
    if (!normalizedEmail) {
      setError('Email is required');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(normalizedEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    if (!password) {
      setError('Password is required');
      return;
    }
    
    setLoading(true);
    
    // ✅ Track authentication state for proper cleanup
    let authSucceeded = false;

    try {
      
      // ✅ Use normalized email but original password (never trim passwords!)
      const userCredential = await signInWithEmailAndPassword(auth, normalizedEmail, password);
      authSucceeded = true;
      const user = userCredential.user;

      // ✅ Check if account exists and is active in Realtime Database
      const userRef = ref(db, `Accounts/${user.uid}`);
      const snapshot = await get(userRef);

      if (!snapshot.exists()) {
        // User exists in Auth but not in DB - sign out and show error
        await auth.signOut();
        setError('Account not properly configured. Please contact support.');
        setLoading(false);
        return;
      }

      const userData = snapshot.val();

      // ✅ Strict account status check - only true is valid
    if (userData.AccountStatus !== true) {

  await signOut(auth);

  navigate('/account-suspended', {
    replace: true
  });

  return;
}



      // ✅ Login successful - navigate to dashboard with replace
      navigate('/dashboard', { replace: true });
      
    } catch (err: unknown) {
      // ✅ If auth succeeded but something else failed, sign out
      if (authSucceeded && auth.currentUser) {
        await auth.signOut().catch(() => {});
      }
      
      // ✅ Type-safe error handling
      if (err instanceof FirebaseError) {
        if (isDevelopment) {
          console.error('Firebase error:', err.code);
        }
        
        // ✅ Security: Handle Firebase errors with generic user-friendly messages
        switch (err.code) {
          // ✅ Group authentication errors with generic message
          case 'auth/invalid-credential':
          case 'auth/user-not-found':
          case 'auth/wrong-password':
            setError('Invalid email or password. Please try again.');
            break;
            
          case 'auth/invalid-email':
            setError('Please enter a valid email address.');
            break;
            
          case 'auth/too-many-requests':
            setError('Too many failed login attempts. Please try again later or reset your password.');
            break;
            
          case 'auth/network-request-failed':
            setError('Network error. Please check your internet connection and try again.');
            break;
            
          case 'auth/user-disabled':
            setError('This account has been disabled. Please contact support.');
            break;
            
          // ✅ Default: Generic error message (no raw Firebase errors)
          default:
            setError('Login failed. Please try again.');
        }
      } else {
        // ✅ Non-Firebase error
        if (isDevelopment) {
          console.error('Unknown error:', err);
        }
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    // ✅ Prevent duplicate submissions
    if (forgotLoading) return;
    
    setForgotError('');
    setForgotSuccess(false);
    
    // ✅ Normalize email
    const normalizedForgotEmail = forgotEmail.trim().toLowerCase();
    
    if (!normalizedForgotEmail) {
      setForgotError('Please enter your email address');
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(normalizedForgotEmail)) {
      setForgotError('Please enter a valid email address');
      return;
    }
    
    setForgotLoading(true);
    
    try {
      // ✅ Send password reset email
      await sendPasswordResetEmail(auth, normalizedForgotEmail);
      
      // ✅ Privacy-preserving: always show success message
      setForgotSuccess(true);
      
      // Clear any existing timeout
      if (forgotTimeoutRef.current) {
        clearTimeout(forgotTimeoutRef.current);
      }
      
      // Close modal after 4 seconds
      forgotTimeoutRef.current = setTimeout(() => {
        setShowForgotModal(false);
        setForgotEmail('');
        setForgotSuccess(false);
        setForgotError('');
        forgotTimeoutRef.current = null;
      }, 4000);
      
    } catch (err: unknown) {
      // ✅ Type-safe error handling
      if (err instanceof FirebaseError) {
        if (isDevelopment) {
          console.error('Password reset error:', err.code);
        }
        
        // ✅ Privacy-preserving: Treat user-not-found as success
        if (err.code === 'auth/user-not-found') {
          setForgotSuccess(true);
          
          // Close modal after 4 seconds
          if (forgotTimeoutRef.current) {
            clearTimeout(forgotTimeoutRef.current);
          }
          forgotTimeoutRef.current = setTimeout(() => {
            setShowForgotModal(false);
            setForgotEmail('');
            setForgotSuccess(false);
            setForgotError('');
            forgotTimeoutRef.current = null;
          }, 4000);
          return;
        }
        
        switch (err.code) {
          case 'auth/invalid-email':
            setForgotError('Please enter a valid email address');
            break;
          case 'auth/too-many-requests':
            setForgotError('Too many requests. Please try again later');
            break;
          default:
            setForgotError('Unable to send reset email. Please try again.');
        }
      } else {
        if (isDevelopment) {
          console.error('Unknown error:', err);
        }
        setForgotError('Unable to send reset email. Please try again.');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotModal = () => {
    if (forgotTimeoutRef.current) {
      clearTimeout(forgotTimeoutRef.current);
      forgotTimeoutRef.current = null;
    }
    setShowForgotModal(false);
    setForgotEmail('');
    setForgotError('');
    setForgotSuccess(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0F1E] flex items-center justify-center px-4">
      {/* ✅ Optimized background effects for mobile */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="hidden md:block absolute top-20 left-10 w-72 h-72 bg-[#00fff5] rounded-full blur-2xl opacity-10"></div>
        <div className="hidden md:block absolute bottom-20 right-10 w-72 h-72 bg-[#7000ff] rounded-full blur-2xl opacity-10"></div>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Back Button */}
        <button 
          onClick={() => navigate('/')} 
          className="mb-6 flex items-center gap-2 text-gray-400 hover:text-white transition group"
          aria-label="Back to home"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition" aria-hidden="true" /> 
          Back to Home
        </button>

        {/* Login Card */}
        <div className="bg-[#1E2338] rounded-2xl border border-[#2A2F45] p-8 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-[#00fff5] to-[#7000ff] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <LogIn className="w-8 h-8 text-white" aria-hidden="true" />
            </div>
            <h1 className="text-3xl font-rajdhani font-bold text-white">Welcome Back!</h1>
            <p className="text-gray-400 mt-2">Login to continue your journey</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5" noValidate>
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-gray-300 mb-2 text-sm font-rajdhani font-bold">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" aria-hidden="true" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-[#0A0F1E] border border-[#2A2F45] rounded-xl px-12 py-3 text-white focus:outline-none focus:border-[#00fff5] transition placeholder:text-gray-600"
                  placeholder="Enter your email"
                  autoComplete="email"
                  aria-label="Email address"
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="block text-gray-300 mb-2 text-sm font-rajdhani font-bold">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" aria-hidden="true" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-[#0A0F1E] border border-[#2A2F45] rounded-xl px-12 py-3 text-white focus:outline-none focus:border-[#00fff5] transition placeholder:text-gray-600"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  aria-label="Password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-gray-500" aria-hidden="true" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-500" aria-hidden="true" />
                  )}
                </button>
              </div>
              <div className="text-right mt-1">
                <button 
                  type="button"
                  onClick={() => setShowForgotModal(true)}
                  className="text-xs text-[#00fff5] hover:underline transition"
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500 rounded-xl p-3 flex items-center gap-2" role="alert">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" aria-hidden="true" />
                <p className="text-red-400 text-sm text-left flex-1">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#00fff5] to-[#7000ff] py-3 rounded-xl text-white font-rajdhani font-bold text-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                  Logging in...
                </span>
              ) : (
                'Login'
              )}
            </button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-400">
              Don't have an account?{' '}
              <Link to="/signup" className="text-[#00fff5] hover:underline font-semibold transition">
                Sign Up
              </Link>
            </p>
          </div>

          {/* Terms */}
          <div className="mt-6 text-center text-xs text-gray-500">
            By logging in, you agree to our{' '}
            <Link to="/terms" className="text-[#00fff5] hover:underline">Terms of Service</Link>
          </div>
        </div>
      </motion.div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="forgot-password-title"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-[#1E2338] rounded-2xl max-w-md w-full border border-[#2A2F45] shadow-2xl"
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 id="forgot-password-title" className="text-white font-rajdhani font-bold text-xl">
                  Reset Password
                </h2>
                <button 
                  onClick={closeForgotModal}
                  className="text-gray-400 hover:text-white transition"
                  aria-label="Close password reset modal"
                >
                  ✕
                </button>
              </div>
              
              <p className="text-gray-400 text-sm mb-4">
                Enter your email address and we'll send you a link to reset your password.
              </p>
              
              <div className="mb-4">
                <label htmlFor="forgot-email" className="block text-gray-300 mb-2 text-sm font-rajdhani font-bold">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" aria-hidden="true" />
                  <input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="w-full bg-[#0A0F1E] border border-[#2A2F45] rounded-xl px-12 py-3 text-white focus:outline-none focus:border-[#00fff5] transition placeholder:text-gray-600"
                    placeholder="Enter your email"
                    aria-label="Email for password reset"
                  />
                </div>
              </div>
              
              {forgotError && (
                <div className="bg-red-500/10 border border-red-500 rounded-xl p-3 mb-4 flex items-center gap-2" role="alert">
                  <AlertCircle className="w-4 h-4 text-red-400" aria-hidden="true" />
                  <p className="text-red-400 text-sm">{forgotError}</p>
                </div>
              )}
              
              {forgotSuccess && (
                <div className="bg-green-500/10 border border-green-500 rounded-xl p-3 mb-4 flex items-center gap-2" role="status">
                  <CheckCircle className="w-4 h-4 text-green-400" aria-hidden="true" />
                  <p className="text-green-400 text-sm">
                    If an account exists with this email, a password reset link has been sent.
                  </p>
                </div>
              )}
              
              <button
                onClick={handleForgotPassword}
                disabled={forgotLoading || forgotSuccess}
                className="w-full bg-gradient-to-r from-[#00fff5] to-[#7000ff] py-3 rounded-xl text-white font-rajdhani font-bold text-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {forgotLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                    Sending...
                  </span>
                ) : (
                  'Send Reset Email'
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Login;